import React from 'react';
import { createPortal } from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Printer, TrendingUp, CheckCircle2, Clock, Layers, AlertCircle, FileText } from 'lucide-react';
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, BarChart, Bar } from 'recharts';

const COLORS = ['#2563eb', '#8b5cf6', '#d946ef', '#f43f5e', '#f59e0b', '#10b981'];

const ReportModal = ({ isOpen, onClose, stats, tickets, teamName, projectId, t }) => {

  const priorityData = stats?.priorityDistribution 
    ? Object.entries(stats.priorityDistribution).map(([name, value]) => ({ 
        id: name,
        name: t(name), 
        value 
      }))
    : [];

  const urgentTickets = (tickets || []).filter(t => t.priority === 'urgent' || t.priority === 'high').slice(0, 5);

  const [isGenerating, setIsGenerating] = React.useState(false);

  const handleDownloadPDF = async () => {
    if (!projectId) return alert("Project ID no disponible");
    setIsGenerating(true);
    try {
      const baseUrl = (import.meta.env.VITE_API_URL || 'http://localhost:5000/api').replace(/\/api$/, '');
      const response = await fetch(`${baseUrl}/api/reports/${projectId}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      if (!response.ok) throw new Error('Falló la generación del PDF. Intenta de nuevo.');
      
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `ViewGate_Report_${(teamName || 'Project').replace(/[^a-zA-Z0-9]/g, '_')}_${new Date().toLocaleDateString().replaceAll('/', '-')}.pdf`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (err) {
      alert(err.message);
    } finally {
      setIsGenerating(false);
    }
  };

  if (!isOpen) return null;

  return createPortal(
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 z-[9999] flex items-center justify-center p-4 sm:p-6 md:p-10 bg-slate-950/80 backdrop-blur-md overflow-y-auto print:static print:bg-white print:p-0 print:block print:overflow-visible">
          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 20 }}
            className="bg-slate-900 border border-white/10 rounded-[32px] w-full max-w-5xl shadow-2xl relative overflow-hidden flex flex-col max-h-[90vh] print:max-h-none print:shadow-none print:border-none print:rounded-none print:w-full print:static print:bg-white print:block print:overflow-visible"
          >
            {/* Header */}
            <div className="p-8 border-b border-white/5 flex items-center justify-between bg-white/[0.02] sticky top-0 z-10 backdrop-blur-xl print:bg-transparent print:border-slate-200">
              <div className="flex items-center gap-4">
                <div className="p-3 rounded-2xl bg-primary/20 text-primary print:text-slate-800">
                  <FileText size={24} />
                </div>
                <div>
                  <h2 className="text-2xl font-display font-black text-slate-100 print:text-black">{t('executiveReport')}</h2>
                  <p className="text-slate-400 text-sm print:text-black">{teamName} • {new Date().toLocaleDateString()}</p>
                </div>
              </div>
              <div className="flex items-center gap-3 print:hidden">
                <button 
                  onClick={handleDownloadPDF}
                  disabled={isGenerating}
                  className="flex items-center gap-2 px-5 py-2.5 bg-primary text-white rounded-xl font-bold text-sm hover:bg-blue-600 transition-all shadow-lg shadow-primary/20 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <FileText size={18} /> {isGenerating ? 'Generando...' : t('downloadPDF')}
                </button>
                <button 
                  onClick={onClose}
                  className="p-2.5 rounded-xl bg-white/5 text-slate-400 hover:text-white transition-colors"
                >
                  <X size={20} />
                </button>
              </div>
            </div>

            {/* Content */}
            <div id="report-content" className="flex-1 overflow-y-auto p-10 space-y-12 print:overflow-visible print:px-0 print:block">
              {/* Executive Summary Cards */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div className="p-6 rounded-3xl bg-white/[0.03] border border-white/5 print:bg-slate-50 print:border-slate-200">
                  <div className="text-slate-500 text-[10px] font-black uppercase tracking-wider mb-2 print:text-black">{t('completionRate')}</div>
                  <div className="text-3xl font-display font-black text-emerald-400 print:text-emerald-700">
                    {stats?.summary?.totalTickets ? `${Math.round((stats.summary.closedTickets / stats.summary.totalTickets) * 100)}%` : '0%'}
                  </div>
                  <p className="text-xs text-slate-500 mt-2 print:text-black">{stats?.summary?.closedTickets || 0} {t('ticketsResolvedTotal')}</p>
                </div>
                <div className="p-6 rounded-3xl bg-white/[0.03] border border-white/5 print:bg-slate-50 print:border-slate-200">
                  <div className="text-slate-500 text-[10px] font-black uppercase tracking-wider mb-2 print:text-black">{t('meanResolution')}</div>
                  <div className="text-3xl font-display font-black text-primary print:text-black">
                    {stats?.summary?.avgResolutionTime || '0d'}
                  </div>
                  <p className="text-xs text-slate-500 mt-2 print:text-black">{t('efficiencyRate')}</p>
                </div>
                <div className="p-6 rounded-3xl bg-white/[0.03] border border-white/5 print:bg-slate-50 print:border-slate-200">
                  <div className="text-slate-500 text-[10px] font-black uppercase tracking-wider mb-2 print:text-black">{t('activeBacklog')}</div>
                  <div className="text-3xl font-display font-black text-amber-400 print:text-amber-800">
                    {stats?.summary?.openTickets || 0}
                  </div>
                  <p className="text-xs text-slate-500 mt-2 print:text-black">{t('pendingActionItems')}</p>
                </div>
              </div>

              {/* Analysis Section */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-10">
                <div className="space-y-6">
                  <h3 className="text-lg font-black text-slate-100 print:text-black border-l-4 border-primary pl-4 uppercase tracking-tight">{t('velocityTrends')}</h3>
                  <div className="h-[250px] w-full bg-white/[0.02] border border-white/5 rounded-3xl p-4 print:border-slate-200 print:bg-white text-slate-300 print:text-black">
                    <ResponsiveContainer width="100%" height="100%">
                      <AreaChart data={stats?.dailyActivity || []}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" vertical={false} opacity={0.5} />
                        <XAxis dataKey="_id" fontSize={10} axisLine={false} tickLine={false} stroke="#000000" tickFormatter={(val) => val.split('-').slice(1).join('/')} />
                        <YAxis fontSize={10} axisLine={false} tickLine={false} stroke="#000000" />
                        <Tooltip 
                          contentStyle={{ backgroundColor: '#0f172a', border: 'none', borderRadius: '12px' }}
                          itemStyle={{ color: '#fff' }}
                          labelStyle={{ color: '#fff' }}
                        />
                        <Area type="monotone" dataKey="created" stroke="#2563eb" fill="#2563eb20" strokeWidth={3} />
                        <Area type="monotone" dataKey="resolved" stroke="#10b981" fill="#10b98120" strokeWidth={3} />
                      </AreaChart>
                    </ResponsiveContainer>
                  </div>
                </div>
                <div className="space-y-6">
                  <h3 className="text-lg font-black text-slate-100 print:text-black border-l-4 border-purple-500 pl-4 uppercase tracking-tight">{t('priorityDistribution')}</h3>
                  <div className="h-[250px] w-full bg-white/[0.02] border border-white/5 rounded-3xl flex items-center print:border-slate-200 print:bg-white">
                    <ResponsiveContainer width="100%" height="100%">
                      <PieChart>
                        <Pie data={priorityData} innerRadius={60} outerRadius={80} paddingAngle={5} dataKey="value">
                          {priorityData.map((entry, index) => {
                            let color = '#64748b';
                            if (entry.id === 'urgent') color = '#EF4444';
                            if (entry.id === 'high') color = '#F59E0B';
                            if (entry.id === 'medium') color = '#6366F1';
                            if (entry.id === 'low') color = '#10B981';
                            return <Cell key={`cell-${index}`} fill={color} />;
                          })}
                        </Pie>
                        <Tooltip 
                          contentStyle={{ backgroundColor: '#0f172a', border: 'none', borderRadius: '12px' }}
                          itemStyle={{ color: '#fff' }}
                          labelStyle={{ color: '#fff' }}
                        />
                      </PieChart>
                    </ResponsiveContainer>
                    <div className="pr-8 space-y-2">
                      {priorityData.map((p, i) => (
                        <div key={p.name} className="flex items-center gap-2 text-xs">
                          <div className="w-2 h-2 rounded-full" style={{ 
                            backgroundColor: 
                              p.id === 'urgent' ? '#EF4444' : 
                              p.id === 'high' ? '#F59E0B' : 
                              p.id === 'medium' ? '#6366F1' : 
                              p.id === 'low' ? '#10B981' : '#64748b' 
                          }} />
                          <span className="text-slate-400 print:text-black font-medium">{p.name}: {p.value}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>

                {/* Workload Distribution in Report */}
                <div className="space-y-6 lg:col-span-2">
                  <div className="flex items-center justify-between">
                    <h3 className="text-lg font-black text-slate-100 print:text-black border-l-4 border-emerald-500 pl-4 uppercase tracking-tight">{t('workloadDistribution')}</h3>
                    <div className="flex items-center gap-4 print:hidden">
                      <div className="flex items-center gap-1.5">
                        <div className="w-2.5 h-2.5 rounded-full bg-blue-500" />
                        <span className="text-[10px] font-black uppercase tracking-wider text-slate-400">{t('createdLabel')}</span>
                      </div>
                      <div className="flex items-center gap-1.5">
                        <div className="w-2.5 h-2.5 rounded-full bg-emerald-500" />
                        <span className="text-[10px] font-black uppercase tracking-wider text-slate-400">{t('resolvedLabel')}</span>
                      </div>
                    </div>
                  </div>
                  {(() => {
                    const creator = stats?.creatorDistribution || {};
                    const resolver = stats?.resolverDistribution || {};
                    const allNames = Array.from(new Set([...Object.keys(creator), ...Object.keys(resolver)]));
                    const workload = allNames
                      .map(name => ({
                        name: name.split(' ')[0],
                        fullName: name,
                        created: creator[name] || 0,
                        resolved: resolver[name] || 0,
                        total: (creator[name] || 0) + (resolver[name] || 0),
                      }))
                      .sort((a, b) => b.total - a.total)
                      .slice(0, 8);
                    
                    if (workload.length === 0) return null;

                    const maxVal = Math.max(...workload.map(w => w.total), 1);

                    return (
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                        {/* Bar chart */}
                        <div className="h-[220px] w-full bg-white/[0.02] border border-white/5 rounded-3xl p-4 print:bg-white print:border-slate-200">
                          <ResponsiveContainer width="100%" height="100%">
                            <BarChart data={workload} barSize={10} barGap={2}>
                              <XAxis dataKey="name" stroke="#64748b" fontSize={10} axisLine={false} tickLine={false} />
                              <YAxis stroke="#64748b" fontSize={10} axisLine={false} tickLine={false} />
                              <Tooltip
                                contentStyle={{ backgroundColor: '#0f172a', border: 'none', borderRadius: '12px' }}
                                itemStyle={{ color: '#fff' }}
                                labelStyle={{ color: '#fff' }}
                              />
                              <Bar dataKey="created" name={t('createdLabel')} fill="#2563eb" radius={[4, 4, 0, 0]} />
                              <Bar dataKey="resolved" name={t('resolvedLabel')} fill="#10b981" radius={[4, 4, 0, 0]} />
                            </BarChart>
                          </ResponsiveContainer>
                        </div>

                        {/* Progress bars breakdown */}
                        <div className="bg-white/[0.02] border border-white/5 rounded-3xl p-6 space-y-4 print:bg-white print:border-slate-200">
                          {workload.slice(0, 5).map(w => {
                            const createdPct = (w.created / maxVal) * 100;
                            const resolvedPct = (w.resolved / maxVal) * 100;
                            return (
                              <div key={w.fullName} className="space-y-1.5">
                                <div className="flex items-center justify-between">
                                  <div className="flex items-center gap-2">
                                    <div className="w-6 h-6 rounded-full bg-primary/10 text-primary flex items-center justify-center text-[10px] font-black border border-primary/20">
                                      {w.name[0]}
                                    </div>
                                    <span className="text-sm font-bold text-slate-300 print:text-black">{w.fullName}</span>
                                  </div>
                                  <div className="flex items-center gap-2 text-[10px] font-black">
                                    <span className="text-blue-400">{w.created}</span>
                                    <span className="text-slate-600">/</span>
                                    <span className="text-emerald-400">{w.resolved}</span>
                                  </div>
                                </div>
                                <div className="h-1.5 bg-white/5 rounded-full overflow-hidden print:bg-slate-100">
                                  <div className="h-full bg-gradient-to-r from-blue-600 to-blue-400 rounded-full" style={{ width: `${createdPct}%` }} />
                                </div>
                                <div className="h-1.5 bg-white/5 rounded-full overflow-hidden print:bg-slate-100">
                                  <div className="h-full bg-gradient-to-r from-emerald-600 to-emerald-400 rounded-full" style={{ width: `${resolvedPct}%` }} />
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    );
                  })()}
                </div>
              </div>

              {/* Critical Tickets */}
              <div className="space-y-6">
                <h3 className="text-lg font-black text-slate-100 print:text-black border-l-4 border-rose-500 pl-4 uppercase tracking-tight">{t('highPriorityAttention')}</h3>
                <div className="space-y-3">
                  {urgentTickets.length > 0 ? urgentTickets.map(ticket => (
                    <div key={ticket._id} className="p-5 rounded-2xl bg-white/[0.03] border border-white/5 flex items-center justify-between print:bg-slate-50 print:border-slate-100">
                      <div className="flex items-center gap-4">
                        <div className="w-2 h-2 rounded-full bg-rose-500 animate-pulse" />
                        <div>
                          <h4 className="font-bold text-slate-100 text-sm print:text-black">{ticket.key || `#${ticket._id.slice(-6).toUpperCase()}`} {ticket.title || ticket.message}</h4>
                          <p className="text-[10px] text-slate-500 uppercase font-black tracking-widest print:text-black">{t('status')}: {t(ticket.status || 'pending')}</p>
                        </div>
                      </div>
                      <div className="text-[10px] text-slate-500 font-medium print:text-black">
                        {new Date(ticket.createdAt).toLocaleDateString()}
                      </div>
                    </div>
                  )) : (
                    <div className="text-center py-10 rounded-2xl bg-emerald-500/5 border border-emerald-500/10 text-emerald-400 print:text-emerald-700">
                      {t('noHighPriorityInsights')}
                    </div>
                  )}
                </div>
              </div>

              {/* Professional Insight */}
              <div className="p-8 rounded-[32px] bg-gradient-to-br from-primary/20 to-purple-500/20 border border-white/10 print:bg-slate-50 print:from-slate-50 print:to-slate-50 print:border-slate-200">
                <div className="flex gap-4">
                  <div className="w-12 h-12 rounded-2xl bg-white/10 text-primary flex items-center justify-center shrink-0">
                    <TrendingUp size={24} />
                  </div>
                  <div>
                    <h4 className="font-bold text-slate-100 print:text-black mb-2">{t('projectHealthInsights')}</h4>
                    <p className="text-sm text-slate-400 leading-relaxed print:text-black font-semibold">
                      {stats?.summary?.openTickets > 10 
                        ? t('reportBacklogWarning')
                        : t('reportCapacityHealthy')} {t('reportStabilityInsight')}
                    </p>
                  </div>
                </div>
              </div>
            </div>

            <div className="p-6 text-center text-[10px] text-slate-600 border-t border-white/5 print:text-black print:border-slate-200">
              Powered by ViewGate Reporting Engine • Generated on {new Date().toISOString()}
            </div>
          </motion.div>
    </motion.div>,
    document.body
  );
};

export default ReportModal;
